import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useProjects } from "@/hooks/useProjects";
import { useRuns } from "@/hooks/useRuns";
import { AppHeader } from "@/components/AppHeader";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ChevronDown, Eye, Mail, Phone, Plus,
} from "lucide-react";
import type { BOMItem } from "@/types/chemistry";

const DEFAULT_SUPPLIERS = [
  "Sigma-Aldrich",
  "TCI Chemicals",
  "Alfa Aesar",
  "Merck KGaA",
  "Thermo Fisher Scientific",
  "Acros Organics",
  "Carl Roth",
  "VWR International",
  "Fluorochem",
  "BLD Pharm",
];

const SupplierCombobox = ({
  value,
  onSelect,
  onCancel,
  knownSuppliers,
}: {
  value: string;
  onSelect: (name: string) => void;
  onCancel: () => void;
  knownSuppliers: string[];
}) => {
  const [search, setSearch] = useState(value);
  const [open, setOpen] = useState(true);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onCancel();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onCancel]);

  const filtered = knownSuppliers.filter(s =>
    s.toLowerCase().includes(search.toLowerCase())
  );
  const exactMatch = knownSuppliers.some(s => s.toLowerCase() === search.trim().toLowerCase());

  return (
    <div ref={ref} className="relative">
      <Input
        className="h-7 text-xs w-36"
        value={search}
        onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
        placeholder="Search supplier…"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter" && search.trim()) onSelect(search.trim());
          if (e.key === "Escape") onCancel();
        }}
      />
      {open && (search.length > 0 || filtered.length > 0) && (
        <div className="absolute top-full left-0 mt-1 w-48 rounded-md border bg-popover text-popover-foreground shadow-md z-50 max-h-48 overflow-y-auto">
          {filtered.map(s => (
            <button
              key={s}
              onClick={() => onSelect(s)}
              className="block w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors"
            >
              {s}
            </button>
          ))}
          {!exactMatch && search.trim() && (
            <button
              onClick={() => onSelect(search.trim())}
              className="block w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors text-primary font-medium border-t"
            >
              <Plus className="h-3 w-3 inline mr-1" />
              Create "{search.trim()}"
            </button>
          )}
          {filtered.length === 0 && !search.trim() && (
            <div className="px-3 py-2 text-xs text-muted-foreground italic">Type to search…</div>
          )}
        </div>
      )}
    </div>
  );
};

function getLeadTimeColor(item: BOMItem): string {
  if (!item.deliveryDays || !item.targetDate) return "text-muted-foreground";
  const target = new Date(item.targetDate);
  const arrival = new Date();
  arrival.setDate(arrival.getDate() + item.deliveryDays);
  const diffDays = (target.getTime() - arrival.getTime()) / (1000 * 60 * 60 * 24);
  if (diffDays < 0) return "text-destructive";
  if (diffDays <= 2) return "text-warning";
  return "text-success";
}

function computeEarliestDelivery(deliveryDays?: number): string {
  if (deliveryDays == null) return "—";
  const d = new Date();
  d.setDate(d.getDate() + deliveryDays);
  return d.toLocaleDateString("en-CA"); // YYYY-MM-DD
}

const SupplierSelection = () => {
  const { projectId, runId } = useParams<{ projectId: string; runId: string }>();
  const navigate = useNavigate();
  const { getProject } = useProjects();
  const { getRun, updateBOMItem } = useRuns();

  const project = getProject(projectId!);
  const run = getRun(runId!);

  const [editingSupplier, setEditingSupplier] = useState<string | null>(null);
  const [customSuppliers, setCustomSuppliers] = useState<string[]>([]);

  if (!project || !run) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="flex items-center justify-center py-20">
          <p className="text-muted-foreground">{!project ? "Compound" : "Run"} not found</p>
        </div>
      </div>
    );
  }

  const allSuppliers = [...new Set([...DEFAULT_SUPPLIERS, ...customSuppliers])];

  const handleSelectSupplier = (itemId: string, name: string) => {
    if (!DEFAULT_SUPPLIERS.includes(name) && !customSuppliers.includes(name)) {
      setCustomSuppliers(prev => [...prev, name]);
    }
    updateBOMItem(run.id, itemId, { supplierName: name, status: "selected" });
    setEditingSupplier(null);
  };

  const handleToggle = (itemId: string, field: "inInventory" | "inStock", current?: boolean) => {
    updateBOMItem(run.id, itemId, { [field]: !current });
  };

  const handlePackageSizeChange = (itemId: string, value: string) => {
    updateBOMItem(run.id, itemId, { packageSize: value });
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-7xl px-6 py-6">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink href="/">Synthesis Plans</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink href={`/project/${project.id}`}>{project.name}</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink href={`/project/${project.id}/run/${run.id}`}>{run.title}</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>Supplier Selection</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold tracking-tight">Supplier Selection</h1>
          <Button variant="outline" size="sm" onClick={() => navigate(`/project/${project.id}/run/${run.id}`)}>
            ← Back to Run
          </Button>
        </div>

        <div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Material</TableHead>
                <TableHead className="text-xs">Qty / Unit</TableHead>
                <TableHead className="text-xs text-center">In Inventory</TableHead>
                <TableHead className="text-xs text-center">In Stock</TableHead>
                <TableHead className="text-xs">Supplier</TableHead>
                <TableHead className="text-xs">Package Size</TableHead>
                <TableHead className="text-xs">Price €</TableHead>
                <TableHead className="text-xs">Lead Time</TableHead>
                <TableHead className="text-xs">Earliest Delivery</TableHead>
                <TableHead className="text-xs">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {run.bomItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-sm text-muted-foreground py-8">
                    No materials in BOM.
                  </TableCell>
                </TableRow>
              ) : (
                run.bomItems.map(item => {
                  const leadColor = getLeadTimeColor(item);
                  return (
                    <TableRow key={item.id}>
                      <TableCell className="text-sm">
                        <span className="font-mono">{item.materialName}</span>
                        {item.casNumber && (
                          <span className="block text-xs text-muted-foreground">{item.casNumber}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{item.quantity.toFixed(2)} {item.unit}</TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={!!item.inInventory}
                          onCheckedChange={() => handleToggle(item.id, "inInventory", item.inInventory)}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={!!item.inStock}
                          onCheckedChange={() => handleToggle(item.id, "inStock", item.inStock)}
                        />
                      </TableCell>
                      <TableCell className="text-sm">
                        {editingSupplier === item.id ? (
                          <SupplierCombobox
                            value={item.supplierName || ""}
                            knownSuppliers={allSuppliers}
                            onSelect={(name) => handleSelectSupplier(item.id, name)}
                            onCancel={() => setEditingSupplier(null)}
                          />
                        ) : (
                          <button
                            onClick={() => setEditingSupplier(item.id)}
                            className="text-xs hover:text-primary transition-colors flex items-center gap-1"
                          >
                            {item.supplierName || <span className="text-muted-foreground italic">Select…</span>}
                            <ChevronDown className="h-3 w-3" />
                          </button>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          className="h-7 text-xs w-24"
                          value={item.packageSize || ""}
                          onChange={(e) => handlePackageSizeChange(item.id, e.target.value)}
                          placeholder="e.g. 100g"
                        />
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.pricePerUnit != null ? `€${(item.pricePerUnit * item.quantity).toFixed(2)}` : "—"}
                      </TableCell>
                      <TableCell className={`text-sm ${leadColor}`}>
                        {item.deliveryDays != null ? `${item.deliveryDays}d` : "—"}
                      </TableCell>
                      <TableCell className={`text-sm ${leadColor}`}>
                        {computeEarliestDelivery(item.deliveryDays)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <button className="p-1 rounded hover:bg-accent transition-colors" title="Details">
                            <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                          <button className="p-1 rounded hover:bg-accent transition-colors" title="Call">
                            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                          <button className="p-1 rounded hover:bg-accent transition-colors" title="Email">
                            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
};

export default SupplierSelection;
